'use client'

import {
  get360Preview,
  getAssetKey,
  getAudioPreview,
  getDetailUrl,
  getDownloadUrl,
  getFormatLabel,
  getImagePreview,
  getPanoramaPreview,
  getResolutionByDimensions,
  getVideoPreview,
  isFootageCrateEffect,
} from '@/lib/assets/assets'
import useAssetDownload, { FormatOption, ResolutionOption } from '@/lib/hooks/useAssetDownload'
import { useEffect, useMemo, useState } from 'react'
import { Asset, AssetSummary, Resolution } from '@/lib/assets/types'
import { ASSET_URL_TYPES } from '@/lib/constants'

/**
 * This hooks handles various the aspects of the asset detail page.
 */
export default function useAssetDetail({
  asset,
  userToken,
  settings,
}: {
  asset?: Asset | AssetSummary
  userToken?: string
  settings?: Record<string, any>
}) {
  const [selectedFormat, setSelectedFormat] = useState<FormatOption | null>(null)
  const [selectedResolution, setSelectedResolution] = useState<ResolutionOption | null>(null)

  const { resolutionIndex, formatIndex, resolutions, downloadOptions, dispatchAssetDownloadEvent } = useAssetDownload({
    asset,
    userToken,
  })

  const assetKey = useMemo(() => {
    return asset ? getAssetKey(asset) : ''
  }, [asset])

  // Construct the download URL
  const downloadUrl = useMemo(() => {
    if (!asset) {
      return null
    }

    return getDownloadUrl({
      asset,
      resolution: selectedResolution?.value,
      format: selectedFormat?.value,
      userToken,
    })
  }, [asset, selectedFormat, selectedResolution, userToken])

  const imagePreview = useMemo(() => {
    if (!asset) {
      return null
    }

    return getImagePreview(asset)
  }, [asset])

  const videoPreview = useMemo(() => {
    if (!asset) {
      return null
    }

    return getVideoPreview(asset)
  }, [asset])

  const asset360Preview = useMemo(() => {
    if (!asset) {
      return null
    }

    return get360Preview(asset)
  }, [asset])

  const panoramaPreview = useMemo(() => {
    if (!asset) {
      return null
    }

    return getPanoramaPreview(asset)
  }, [asset])

  const audioPreview = useMemo(() => {
    if (!asset) {
      return null
    }

    return getAudioPreview(asset)
  }, [asset])

  // Available formats for the asset (if any) taking into account the selected resolution
  const formats: FormatOption[] = useMemo(() => {
    if (!asset || !('dl_opts' in asset) || !asset.dl_opts || formatIndex === -1) {
      return []
    }

    // Extract the format values from the asset.dl_opts.items,
    // matching the selected resolution and preventing duplicates.
    const formatValues = asset.dl_opts.items
      .filter((item) => !selectedResolution || item.variant_labels[resolutionIndex] === selectedResolution.value)
      .filter((value) => !!value)
      .map((item) => item.variant_labels[formatIndex])
      .filter((value, index, self) => self.findIndex((v) => v === value) === index)

    return formatValues.map((value) => ({
      label: getFormatLabel(value),
      value: value,
    }))
  }, [asset, formatIndex, resolutionIndex, selectedResolution])

  const resolution: Resolution | null = useMemo(() => {
    if (!asset || !('dimensions' in asset)) {
      return null
    }

    return getResolutionByDimensions(asset.dimensions ?? '')
  }, [asset])

  const is4K = useMemo(() => {
    if (!asset) {
      return false
    }

    return isFootageCrateEffect(asset) && resolution?.id === '4k'
  }, [asset, resolution?.id])

  const isLightBackground = useMemo(() => asset?.background === 'light', [asset])

  // Generate asset detail link using legacy URL.
  // E.g.: https://vfx.productioncrate.com/video-effects/FootageCrate-4K_World_Breaker_Shockwave_Angled_8
  const legacyAssetDetailLink = useMemo(() => {
    if (!asset) {
      return null
    }

    return getDetailUrl(asset)
  }, [asset])

  // Reset state when assetId changes
  useEffect(() => {
    setSelectedResolution(null)
    setSelectedFormat(null)
  }, [assetKey])

  // Select a first resolution option by default
  useEffect(() => {
    if (resolutions.length > 0 && !selectedResolution) {
      setSelectedResolution(resolutions[0])
    }
  }, [resolutions, selectedResolution])

  // Reset a selected format when resolution changes
  useEffect(() => {
    setSelectedFormat(null)
  }, [selectedResolution])

  // Select a format option by default. Use the preferred format from the settings if available.
  useEffect(() => {
    if (formats.length > 0) {
      const preferredFormat = !!settings?.videoFormat
        ? formats.find((format) => format.value.toLowerCase() === `${settings.videoFormat}`.toLowerCase())
        : null

      setSelectedFormat(preferredFormat ?? formats[0])
    }
  }, [formats, settings])

  // Asset detail URL
  const assetDetailUrl = useMemo(() => {
    if (!assetKey) {
      return ''
    }

    let basePath = ASSET_URL_TYPES[asset?.__typename as keyof typeof ASSET_URL_TYPES]
    if (asset && 'music' in asset && asset.music && !!+asset.music) {
      basePath = ASSET_URL_TYPES.sc_sounds[0]
    }
    if (asset && 'music' in asset && asset.music && !+asset.music) {
      basePath = ASSET_URL_TYPES.sc_sounds[1]
    }

    if (!basePath) {
      return ''
    }

    return `${basePath}/${assetKey}`
  }, [asset, assetKey])

  return {
    assetKey,
    assetDetailUrl,
    downloadUrl,
    selectedFormat,
    setSelectedFormat,
    selectedResolution,
    setSelectedResolution,
    resolutionIndex,
    formatIndex,
    resolutions,
    formats,
    downloadOptions,
    dispatchAssetDownloadEvent,
    imagePreview,
    videoPreview,
    asset360Preview,
    panoramaPreview,
    audioPreview,
    resolution,
    is4K,
    isLightBackground,
    legacyAssetDetailLink,
    getResolutionByDimensions,
  }
}
